import { Alert, Box, Link, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import PasswordStrengthIndicator from 'components/common/PasswordStrengthIndicator/PasswordStrengthIndicator';
import AuthLayout, { authSubmitButtonSx } from 'views/v2/auth/components/authLayout';

import api from 'api';
import { useInflight } from 'hooks/useInflight';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { AppDispatch } from 'redux/store';
import type { ResetPasswordInterface } from 'types';
import { resetPasswordDefaultValues, resetPasswordValidationSchema } from 'utils/validations/auth';

type ErrorView = null | 'missing' | 'expired';

// Public page reached from the one-time link in the password-reset email:
//   /reset-password?token=<token>&email=<urlencoded-email>[&lang=fr]
// The token is the source of truth and is sensitive — it is never logged and
// only forwarded straight to POST /api/reset_password. The `email` query param
// is for display/prefill context only.
export default function ResetPasswordV2() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token') ?? '';
    const email = searchParams.get('email') ?? '';

    // The reset email is bilingual; allow an optional `?lang=` override so a
    // French recipient lands on a French page. Falls back to the app's
    // configured language (EN by default for unauthenticated visitors).
    const langParam = (searchParams.get('lang') ?? '').toUpperCase();
    const { translate } = useTranslate(langParam);

    const [apiError, setApiError] = useState('');
    const [errorView, setErrorView] = useState<ErrorView>(token ? null : 'missing');

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: '/reset-password',
            title: 'ResetPassword',
        });
    }, []);

    const goToForgotPassword = () => navigate('/password-recovery');

    // In-flight gated submit: a double-click / Enter race would otherwise fire
    // two POST /api/reset-password calls. The hook drops the second while the
    // first is pending; the disabled button + spinner is the visible half.
    const { pending: submitting, run: runSubmit } = useInflight(
        async (_signal, values: ResetPasswordInterface) => {
            if (!token) {
                setErrorView('missing');
                return;
            }
            setApiError('');
            try {
                await api.resetPassword.post({ token, password: values.password });
                dispatch(
                    setAlert({ message: translate('passwordResetLoginPrompt'), type: 'success' })
                );
                navigate('/login');
            } catch (err) {
                const response = (err as AxiosError<{ msg?: string }>)?.response;
                const status = response?.status;
                if (status === 400) {
                    setErrorView('expired');
                } else if (status === 403) {
                    // Prefer the server's exact policy message when present so the
                    // inline error always matches the backend verbatim.
                    setApiError(response?.data?.msg || translate('passwordPolicy'));
                } else if (status === 429) {
                    setApiError(translate('tooManyAttempts'));
                } else {
                    setApiError(translate('somethingWentWrong'));
                }
            }
        }
    );

    const handleSubmit = async (values: ResetPasswordInterface) => {
        await runSubmit(values);
    };

    const backToLogin = (
        <Link
            component={RouterLink}
            to="/login"
            underline="hover"
            variant="body2"
            onClick={(e) => {
                e.preventDefault();
                navigate('/login');
            }}
            sx={{
                fontWeight: 600,
                color: 'primary.main',
                py: 0.5,
            }}
        >
            {translate('backToLogin')}
        </Link>
    );

    if (errorView) {
        return (
            <AuthLayout
                title={translate('resetLinkInvalidTitle')}
                subtitle={
                    errorView === 'missing'
                        ? translate('resetLinkMissingBody')
                        : translate('resetLinkInvalidBody')
                }
                footer={backToLogin}
            >
                <SubmitButton
                    type="button"
                    fullWidth
                    size="large"
                    sx={authSubmitButtonSx}
                    onClick={goToForgotPassword}
                >
                    requestNewLink
                </SubmitButton>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title={translate('resetYourPassword')}
            subtitle={translate('resetPasswordSubtitle')}
            footer={backToLogin}
        >
            <Formik
                initialValues={resetPasswordDefaultValues}
                validationSchema={resetPasswordValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values }) => {
                    const busy = isSubmitting || submitting;
                    return (
                        <Form noValidate aria-busy={busy}>
                            <Stack spacing={2}>
                                {email ? (
                                    <Box
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                            bgcolor: 'action.hover',
                                            borderRadius: 1.5,
                                            px: 2,
                                            py: 1.25,
                                            wordBreak: 'break-all',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {email}
                                    </Box>
                                ) : null}

                                <FormField
                                    name="password"
                                    label="newPassword"
                                    type="password"
                                    disabled={busy}
                                    autoComplete="new-password"
                                />

                                <PasswordStrengthIndicator
                                    password={values.password}
                                    lng={langParam}
                                />

                                <FormField
                                    name="confirmPassword"
                                    label="confirmNewPassword"
                                    type="password"
                                    disabled={busy}
                                    autoComplete="new-password"
                                />

                                {apiError ? (
                                    <Alert
                                        severity="error"
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 2,
                                            py: 0.5,
                                            '& .MuiAlert-message': {
                                                width: '100%',
                                                textAlign: 'center',
                                            },
                                        }}
                                    >
                                        {apiError}
                                    </Alert>
                                ) : null}

                                <SubmitButton
                                    loading={busy}
                                    disabled={busy}
                                    fullWidth
                                    size="large"
                                    sx={authSubmitButtonSx}
                                >
                                    resetPasswordButton
                                </SubmitButton>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </AuthLayout>
    );
}
